// File: src/components/admin/PlansView.js
// world-class Plans editor.
//
// Lists all plans (archived split into a collapsed section), provides
// "New plan" creation, and delegates per-plan editing to PlanEditor (which
// owns marketing/caps/Stripe/preview/cost/diff/history).
//
// Replaces the simple 5-cap editor.

import React, { useEffect, useState } from 'react';
import {
    Box, Stack, Typography, Button, CircularProgress, Alert, Accordion,
    AccordionSummary, AccordionDetails, Chip,
} from '@mui/material';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

import { mainApi } from '../../services/mainApi';
import PlanEditor from './plans/PlanEditor';
import CreatePlanDialog from './plans/CreatePlanDialog';

export default function PlansView() {
    const [plans, setPlans] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [createOpen, setCreateOpen] = useState(false);

    async function reload() {
        try {
            setError(null);
            const data = await mainApi.listPlans();
            setPlans(Array.isArray(data) ? data : []);
        } catch (e) {
            setError(e?.response?.data?.error || e.message);
        }
    }

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        mainApi.listPlans()
            .then((data) => { if (!cancelled) setPlans(Array.isArray(data) ? data : []); })
            .catch((e) => { if (!cancelled) setError(e?.response?.data?.error || e.message); })
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, []);

    function handlePlanChanged(updated, opts = {}) {
        // Refresh from server when something fundamental shifted (create,
        // archive flip changes which section it lives in). Otherwise patch
        // in place so the editor doesn't lose its draft state.
        if (opts.wasCreated) {
            reload();
            return;
        }
        setPlans(ps => {
            const idx = ps.findIndex(p => p.id === updated.id);
            if (idx === -1) return [...ps, updated];
            const copy = [...ps];
            copy[idx] = updated;
            return copy;
        });
    }

    function handleCreated(plan) {
        setPlans(ps => [...ps, plan]);
    }

    if (loading) {
        return <Box sx={{ p: 4, textAlign: 'center' }}><CircularProgress /></Box>;
    }

    const active = plans.filter(p => !p.archived);
    const archived = plans.filter(p => p.archived);

    return (
        <Box data-testid="plans-view" sx={{ p: 3 }}>
            <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }}>
                <Box sx={{ flex: 1 }}>
                    <Typography variant="h4" sx={{ fontWeight: 600, mb: 0.5 }}>Plans</Typography>
                    <Typography variant="body2" color="text.secondary">
                        Edit caps, marketing copy, and Stripe wiring per plan. Save shows a confirmation
                        diff before changes propagate to every user on the plan.
                    </Typography>
                </Box>
                <Button
                    variant="contained"
                    startIcon={<AddCircleIcon />}
                    onClick={() => setCreateOpen(true)}
                    data-testid="new-plan-btn"
                >
                    New plan
                </Button>
            </Stack>

            {error && (
                <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>
            )}

            <Stack spacing={3}>
                {active.map((p) => (
                    <PlanEditor key={p.id} plan={p} onPlanChanged={handlePlanChanged} />
                ))}
                {active.length === 0 && (
                    <Alert severity="info">No active plans. Create one above, or unarchive an existing plan below.</Alert>
                )}
            </Stack>

            {archived.length > 0 && (
                <Accordion sx={{ mt: 4 }} data-testid="archived-plans-accordion">
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Typography sx={{ fontWeight: 600 }}>Archived</Typography>
                        <Chip size="small" label={archived.length} sx={{ ml: 1.5 }} />
                    </AccordionSummary>
                    <AccordionDetails>
                        <Stack spacing={3}>
                            {archived.map((p) => (
                                <PlanEditor key={p.id} plan={p} onPlanChanged={handlePlanChanged} />
                            ))}
                        </Stack>
                    </AccordionDetails>
                </Accordion>
            )}

            <CreatePlanDialog
                open={createOpen}
                onClose={() => setCreateOpen(false)}
                onCreated={handleCreated}
            />
        </Box>
    );
}
